<style scoped>
.phone-form {
	max-width: 600px;
	margin: 0 auto;
	padding: 1rem;
}

.form-section {
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 1px solid #dee2e6;
}

.form-section:last-child {
	border-bottom: none;
}

.form-section h6 {
	color: #495057;
	margin-bottom: 1rem;
	font-weight: 600;
}

.report-type-badge {
	cursor: pointer;
	transition: all 0.2s;
}

.report-type-badge:hover {
	opacity: 0.8;
}

.report-type-badge.active {
	transform: scale(1.05);
}

.checkbox-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 0.5rem;
}

@media (max-width: 576px) {
	.checkbox-grid {
		grid-template-columns: 1fr;
	}
}
</style>

<template>
	<div class="phone-form">
		<div class="d-flex align-items-center justify-content-between mb-3">
			<h5 class="mb-0">
				<i class="bi bi-telephone-fill me-2 text-primary"></i>
				Phone Intake Report
			</h5>
			<button class="btn btn-sm btn-outline-secondary" @click="$emit('cancel')">
				<i class="bi bi-x-lg"></i> Cancel
			</button>
		</div>

		<form @submit.prevent="handleSubmit">
			<!-- Report Type -->
			<div class="form-section">
				<h6>Report Type</h6>
				<div class="btn-group w-100" role="group">
					<input
						type="radio"
						class="btn-check"
						id="type-compliance"
						value="compliance"
						v-model="reportType"
						autocomplete="off"
					/>
					<label
						class="btn btn-outline-success report-type-badge"
						:class="{ active: reportType === 'compliance' }"
						for="type-compliance"
					>
						<i class="bi bi-check-circle"></i> Compliance
					</label>

					<input
						type="radio"
						class="btn-check"
						id="type-nuisance"
						value="nuisance"
						v-model="reportType"
						autocomplete="off"
					/>
					<label
						class="btn btn-outline-danger report-type-badge"
						:class="{ active: reportType === 'nuisance' }"
						for="type-nuisance"
					>
						<i class="bi bi-mosquito"></i> Nuisance
					</label>

					<input
						type="radio"
						class="btn-check"
						id="type-water"
						value="water"
						v-model="reportType"
						autocomplete="off"
					/>
					<label
						class="btn btn-outline-info report-type-badge"
						:class="{ active: reportType === 'water' }"
						for="type-water"
					>
						<i class="bi bi-droplet"></i> Water
					</label>
				</div>
			</div>

			<!-- Address -->
			<div class="form-section">
				<h6>Address <span class="text-danger">*</span></h6>
				<AddressSuggestion
					v-model="address"
					placeholder="Type an address..."
					:class="{ 'is-invalid': errors.address }"
				/>
				<div class="invalid-feedback d-block" v-if="errors.address">
					{{ errors.address }}
				</div>
			</div>

			<!-- Reporter Info -->
			<div class="form-section">
				<h6>Reporter Information</h6>
				<div class="mb-2">
					<label class="form-label small">Name</label>
					<input
						type="text"
						class="form-control"
						placeholder="Caller's name"
						v-model="reporterName"
					/>
				</div>
				<div class="row">
					<div class="col-md-6 mb-2">
						<label class="form-label small">Phone</label>
						<input
							type="tel"
							class="form-control"
							:class="{ 'is-invalid': errors.phone }"
							placeholder="(555) 123-4567"
							v-model="reporterPhone"
						/>
						<div class="invalid-feedback" v-if="errors.phone">
							{{ errors.phone }}
						</div>
					</div>
					<div class="col-md-6 mb-2">
						<label class="form-label small">Email</label>
						<input
							type="email"
							class="form-control"
							placeholder="caller@example.com"
							v-model="reporterEmail"
						/>
					</div>
				</div>
			</div>

			<!-- Comments (common to all types) -->
			<div class="form-section">
				<h6>Details</h6>
				<div class="mb-2">
					<label class="form-label small">Comments</label>
					<textarea
						class="form-control"
						rows="3"
						placeholder="Description of the issue..."
						v-model="comments"
					></textarea>
				</div>
			</div>

			<!-- Compliance-specific fields -->
			<div class="form-section" v-if="reportType === 'compliance'">
				<h6>Compliance Details</h6>
				<div class="mb-2">
					<label class="form-label small">Property access</label>
					<select class="form-select" v-model="permissionType">
						<option value="unselected">Unselected</option>
						<option value="denied">Denied</option>
						<option value="granted">Granted</option>
						<option value="with-owner">With owner</option>
					</select>
				</div>
				<div class="mb-2">
					<label class="form-label small">Access Instructions</label>
					<input
						type="text"
						class="form-control"
						placeholder="How to access the property"
						v-model="accessInstructions"
					/>
				</div>
				<div class="mb-2">
					<label class="form-label small">Availability Notes</label>
					<input
						type="text"
						class="form-control"
						placeholder="Best times to find someone home"
						v-model="availabilityNotes"
					/>
				</div>
				<div class="mb-2">
					<label class="form-label small">Gate Code</label>
					<input
						type="text"
						class="form-control"
						placeholder="Gate or security code"
						v-model="gateCode"
					/>
				</div>
				<div class="checkbox-grid">
					<div class="form-check">
						<input
							type="checkbox"
							class="form-check-input"
							id="has-dog"
							v-model="hasDog"
						/>
						<label class="form-check-label small" for="has-dog">
							Dog on property
						</label>
					</div>
					<div class="form-check">
						<input
							type="checkbox"
							class="form-check-input"
							id="wants-scheduled"
							v-model="wantsScheduled"
						/>
						<label class="form-check-label small" for="wants-scheduled">
							Wants scheduled visit
						</label>
					</div>
				</div>
			</div>

			<!-- Nuisance-specific fields -->
			<div class="form-section" v-if="reportType === 'nuisance'">
				<h6>Nuisance Details</h6>
				<div class="mb-2">
					<label class="form-label small">Duration</label>
					<select class="form-select" v-model="nuisanceDuration">
						<option value="none">None</option>
						<option value="just-noticed">Just noticed</option>
						<option value="few-days">A few days</option>
						<option value="1-2-weeks">1-2 weeks</option>
						<option value="2-4-weeks">2-4 weeks</option>
						<option value="1-3-months">1-3 months</option>
						<option value="seasonal">Seasonal</option>
					</select>
				</div>
				<div class="mb-2">
					<label class="form-label small">Source</label>
					<div class="checkbox-grid">
						<div class="form-check">
							<input
								type="checkbox"
								class="form-check-input"
								id="source-container"
								v-model="sourceContainer"
							/>
							<label class="form-check-label small" for="source-container">
								Container
							</label>
						</div>
						<div class="form-check">
							<input
								type="checkbox"
								class="form-check-input"
								id="source-gutter"
								v-model="sourceGutter"
							/>
							<label class="form-check-label small" for="source-gutter">
								Gutter
							</label>
						</div>
						<div class="form-check">
							<input
								type="checkbox"
								class="form-check-input"
								id="source-stagnant"
								v-model="sourceStagnant"
							/>
							<label class="form-check-label small" for="source-stagnant">
								Stagnant water
							</label>
						</div>
					</div>
				</div>
				<div class="mb-2">
					<label class="form-label small">Source Description</label>
					<input
						type="text"
						class="form-control"
						placeholder="Describe the mosquito source"
						v-model="sourceDescription"
					/>
				</div>
				<div class="mb-2">
					<label class="form-label small">Location</label>
					<div class="checkbox-grid">
						<div class="form-check">
							<input
								type="checkbox"
								class="form-check-input"
								id="loc-frontyard"
								v-model="isLocationFrontyard"
							/>
							<label class="form-check-label small" for="loc-frontyard">
								Front yard
							</label>
						</div>
						<div class="form-check">
							<input
								type="checkbox"
								class="form-check-input"
								id="loc-backyard"
								v-model="isLocationBackyard"
							/>
							<label class="form-check-label small" for="loc-backyard">
								Back yard
							</label>
						</div>
					</div>
				</div>
				<div class="mb-2">
					<label class="form-label small">Additional Info</label>
					<textarea
						class="form-control"
						rows="2"
						placeholder="Any additional details..."
						v-model="additionalInfo"
					></textarea>
				</div>
			</div>

			<!-- Water-specific fields -->
			<div class="form-section" v-if="reportType === 'water'">
				<h6>Standing Water Details</h6>
				<div class="mb-2">
					<label class="form-label small">Duration</label>
					<select class="form-select" v-model="waterDuration">
						<option value="none">None</option>
						<option value="just-noticed">Just noticed</option>
						<option value="few-days">A few days</option>
						<option value="1-2-weeks">1-2 weeks</option>
						<option value="2-4-weeks">2-4 weeks</option>
						<option value="1-3-months">1-3 months</option>
						<option value="seasonal">Seasonal</option>
					</select>
				</div>
				<div class="mb-2">
					<label class="form-label small">Mosquito Development</label>
					<div class="checkbox-grid">
						<div class="form-check">
							<input
								type="checkbox"
								class="form-check-input"
								id="has-larvae"
								v-model="hasLarvae"
							/>
							<label class="form-check-label small" for="has-larvae">
								Larvae
							</label>
						</div>
						<div class="form-check">
							<input
								type="checkbox"
								class="form-check-input"
								id="has-pupae"
								v-model="hasPupae"
							/>
							<label class="form-check-label small" for="has-pupae">
								Pupae
							</label>
						</div>
						<div class="form-check">
							<input
								type="checkbox"
								class="form-check-input"
								id="has-adult"
								v-model="hasAdult"
							/>
							<label class="form-check-label small" for="has-adult">
								Adult mosquitoes
							</label>
						</div>
					</div>
				</div>
				<div class="mb-2">
					<label class="form-label small">Property Owner (if known)</label>
					<input
						type="text"
						class="form-control mb-1"
						placeholder="Owner name"
						v-model="ownerName"
					/>
					<div class="row">
						<div class="col-md-6 mb-1">
							<input
								type="tel"
								class="form-control"
								:class="{ 'is-invalid': errors.ownerPhone }"
								placeholder="Owner phone"
								v-model="ownerPhone"
							/>
							<div class="invalid-feedback" v-if="errors.ownerPhone">
								{{ errors.ownerPhone }}
							</div>
						</div>
						<div class="col-md-6 mb-1">
							<input
								type="email"
								class="form-control"
								placeholder="Owner email"
								v-model="ownerEmail"
							/>
						</div>
					</div>
				</div>
			</div>

			<!-- Submit -->
			<div class="d-grid gap-2 mt-3">
				<button
					type="submit"
					class="btn btn-primary"
					:disabled="submitting || (!address.gid && !address.raw.trim())"
				>
					<span
						class="spinner-border spinner-border-sm me-1"
						v-if="submitting"
					></span>
					<i class="bi bi-save me-1" v-else></i>
					{{ submitting ? "Submitting..." : "Submit Phone Report" }}
				</button>
			</div>
		</form>

		<!-- Toast notification -->
		<div
			class="toast-container position-fixed bottom-0 end-0 p-3"
			style="z-index: 9999"
		>
			<div
				class="toast align-items-center text-bg-success border-0"
				:class="{ show: toastShow }"
				role="alert"
			>
				<div class="d-flex">
					<div class="toast-body">
						<i class="bi bi-check-circle me-1"></i>
						{{ toastMessage }}
					</div>
					<button
						type="button"
						class="btn-close btn-close-white me-2 m-auto"
						@click="toastShow = false"
					></button>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { apiClient } from "@/client";
import AddressSuggestion from "@/components/AddressSuggestion.vue";
import { phoneValidationError } from "@/phone";
import { Address } from "@/type/api";

interface Emits {
	(e: "cancel"): void;
	(e: "created"): void;
}

const emit = defineEmits<Emits>();

// Form state
const reportType = ref<string>("compliance");
const address = ref<Address>(new Address());
const reporterName = ref<string>("");
const reporterPhone = ref<string>("");
const reporterEmail = ref<string>("");
const comments = ref<string>("");

// Compliance-specific
const accessInstructions = ref<string>("");
const availabilityNotes = ref<string>("");
const gateCode = ref<string>("");
const hasDog = ref<boolean>(false);
const wantsScheduled = ref<boolean>(false);
const permissionType = ref<string>("unselected");

// Nuisance-specific
const nuisanceDuration = ref<string>("none");
const isLocationBackyard = ref<boolean>(false);
const isLocationFrontyard = ref<boolean>(false);
const sourceContainer = ref<boolean>(false);
const sourceGutter = ref<boolean>(false);
const sourceStagnant = ref<boolean>(false);
const sourceDescription = ref<string>("");
const additionalInfo = ref<string>("");

// Water-specific
const hasAdult = ref<boolean>(false);
const hasLarvae = ref<boolean>(false);
const hasPupae = ref<boolean>(false);
const ownerName = ref<string>("");
const ownerPhone = ref<string>("");
const ownerEmail = ref<string>("");
const waterDuration = ref<string>("none");

// UI state
const submitting = ref<boolean>(false);
const toastShow = ref<boolean>(false);
const toastMessage = ref<string>("");
const errors = ref<Record<string, string>>({});

async function handleSubmit() {
	errors.value = {};

	// Validate
	if (!address.value.gid && !address.value.raw.trim()) {
		errors.value.address = "Address is required";
		return;
	}
	errors.value.phone = (await phoneValidationError(reporterPhone.value)) ?? "";
	if (errors.value.phone) {
		return;
	}
	errors.value.ownerPhone =
		(await phoneValidationError(ownerPhone.value)) ?? "";
	if (errors.value.ownerPhone) {
		return;
	}

	submitting.value = true;

	try {
		const body: Record<string, any> = {
			report_type: reportType.value,
			address_raw: address.value.raw,
			address_gid: address.value.gid,
			reporter_name: reporterName.value,
			reporter_phone: reporterPhone.value,
			reporter_email: reporterEmail.value,
			comments: comments.value,
		};

		// Add type-specific fields
		if (reportType.value === "compliance") {
			if (accessInstructions.value)
				body.access_instructions = accessInstructions.value;
			if (availabilityNotes.value)
				body.availability_notes = availabilityNotes.value;
			if (gateCode.value) body.gate_code = gateCode.value;
			body.has_dog = hasDog.value;
			body.wants_scheduled = wantsScheduled.value;
			body.permission_type = permissionType.value;
		} else if (reportType.value === "nuisance") {
			if (nuisanceDuration.value)
				body.nuisance_duration = nuisanceDuration.value;
			body.is_location_backyard = isLocationBackyard.value;
			body.is_location_frontyard = isLocationFrontyard.value;
			body.source_container = sourceContainer.value;
			body.source_gutter = sourceGutter.value;
			body.source_stagnant = sourceStagnant.value;
			if (sourceDescription.value)
				body.source_description = sourceDescription.value;
			if (additionalInfo.value) body.additional_info = additionalInfo.value;
		} else if (reportType.value === "water") {
			body.has_larvae = hasLarvae.value;
			body.has_pupae = hasPupae.value;
			body.has_adult = hasAdult.value;
			if (ownerName.value) body.owner_name = ownerName.value;
			if (ownerPhone.value) body.owner_phone = ownerPhone.value;
			if (ownerEmail.value) body.owner_email = ownerEmail.value;
			body.water_duration = waterDuration.value;
		}

		await apiClient.JSONPost("/api/communication/phone-report", body);
		toastMessage.value = "Phone report submitted successfully!";
		toastShow.value = true;
		setTimeout(() => {
			toastShow.value = false;
		}, 3000);
		emit("created");
	} catch (e: any) {
		const msg = e instanceof Error ? e.message : "Failed to submit report";
		errors.value.address = msg;
	} finally {
		submitting.value = false;
	}
}
</script>
